import React, { useEffect, useState } from 'react';

const GoogleAuth = ({ onSignIn, onSignOut, user }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const isDevelopment = process.env.REACT_APP_ENV === 'development';
  const useLocalStack = process.env.REACT_APP_USE_LOCALSTACK === 'true';

  useEffect(() => {
    if (isDevelopment) {
      setIsInitialized(true);
      return;
    }

    const initializeGoogleAuth = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        setIsInitialized(true);

        // Render the sign-in button with a small delay to ensure DOM is ready
        if (!user) {
          setTimeout(() => {
            const buttonElement = document.getElementById('google-signin-button');
            if (buttonElement) {
              window.google.accounts.id.renderButton(
                buttonElement,
                {
                  theme: 'outline',
                  size: 'large',
                  type: 'standard',
                  text: 'sign_in_with',
                  shape: 'rectangular',
                  logo_alignment: 'left',
                }
              );
            }
          }, 100);
        }
      } else {
        // Google Identity Services not loaded yet, try again
        setTimeout(initializeGoogleAuth, 100);
      }
    };

    initializeGoogleAuth();
  }, [user, isDevelopment]);

  const handleCredentialResponse = async (response) => {
    setError(''); // Clear any previous errors
    setIsLoading(true);
    try {
      // Decode the JWT token to get user information
      const userInfo = parseJwt(response.credential);
      
      const userData = {
        user_id: userInfo.sub,
        email: userInfo.email,
        name: userInfo.name,
        picture: userInfo.picture,
        token: response.credential
      };

      await onSignIn(userData);
      setError(''); // Clear any previous errors on successful sign-in
      setIsLoading(false);
    } catch (error) {
      // Error handling Google sign-in
      setError(error.message || 'Failed to sign in. Please try again.');
      setIsLoading(false);
    }
  };

  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      // Error parsing JWT
      return null;
    }
  };

  const handleDemoSignIn = async () => {
    setIsLoading(true);
    setError(''); // Clear any previous errors
    
    // Simulate a demo user for local development
    const demoUserData = {
      user_id: 'demo_user_12345',
      email: 'demo@example.com',
      name: 'Demo User',
      picture: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiByeD0iMTYiIGZpbGw9IiM0Mjg1RjQiLz4KPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJzeXN0ZW0tdWkiIGZvbnQtc2l6ZT0iMTQiIGZvbnQtd2VpZ2h0PSI1MDAiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIj5EVTwvdGV4dD4KPHN2Zz4=',
      token: 'demo-token'
    };

    // Simulate API delay
    setTimeout(async () => {
      try {
        await onSignIn(demoUserData);
        setError(''); // Clear any previous errors on successful sign-in
      } catch (error) {
        setError(error.message || 'Failed to sign in. Please try again.');
      }
      setIsLoading(false);
    }, 1000);
  };

  const handleSignOut = () => {
    if (!isDevelopment && window.google && window.google.accounts) {
      window.google.accounts.id.disableAutoSelect();
    }
    onSignOut();
  };

  if (!isInitialized) {
    return <div className="loading">Loading Google Authentication...</div>;
  }

  if (user) {
    return (
      <div className="user-info">
        <img 
          src={user.picture} 
          alt="Profile" 
          style={{ width: '32px', height: '32px', borderRadius: '50%', marginRight: '10px' }}
        />
        <span>{user.name}</span>
        <button onClick={handleSignOut} className="logout-button">
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="card">
        <h1 className="title">🐝 Spelling Bee Practice</h1>
        <p className="subtitle">
          Practice spelling bee words with spaced repetition learning
        </p>
        
        {error && (
          <div style={{ 
            color: '#e53e3e', 
            backgroundColor: '#fed7d7', 
            padding: '12px', 
            borderRadius: '6px', 
            marginBottom: '1rem',
            border: '1px solid #feb2b2'
          }}>
            {error}
          </div>
        )}
        
        {isDevelopment ? (
          <>
            <button 
              onClick={handleDemoSignIn} 
              className="google-signin"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : useLocalStack ? '🐳 LocalStack Demo' : '🚀 Mock Demo'}
            </button>
            <p style={{ fontSize: '0.9rem', color: '#718096', marginTop: '1rem' }}>
              {useLocalStack ? 'Using LocalStack AWS emulation' : 'Demo mode for local testing'}
            </p>
          </>
        ) : (
          <>
            <div id="google-signin-button" style={{ display: 'flex', justifyContent: 'center' }}></div>
            <p style={{ fontSize: '0.9rem', color: '#718096', marginTop: '1rem' }}>
              Sign in with Google to start practicing
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleAuth;